import { relationOf, stemElement, type Relation } from '../ganzhi'
import { bandOf, compatibility, type CompatResult, type CompatSide } from '../compat'

/**
 * 관계별 궁합. 같은 두 사람이라도 **무슨 사이인지에 따라 볼 자리가 다르다.**
 *
 * 기본 궁합(합·충·오행 보완)은 그대로 쓰고, 관계마다 중요한 기운에 가중치를 더한다.
 *  - 상사: 규칙과 평가의 기운(관성)이 서 있어야 편하다
 *  - 동료·부하: 어깨를 나란히 하는 기운(비겁)
 *  - 부모: 받쳐 주는 기운(인성) / 자녀: 내가 내보내는 기운(식상)
 *  - 사업 파트너: 돈을 같이 다루는 기운(재성)과 나란한 기운(비겁)
 *  - 친구: 비겁과 식상
 *  - 연인·배우자: 기본 궁합 그대로 (합·충이 가장 크게 작용하는 자리)
 *
 * 관계를 고르면 **어떤 풀이가 나올지도 함께 정해진다** — 연애 쪽이면 자세한 연애 궁합,
 * 나머지는 간단한 일반 궁합. 사용자가 따로 고르지 않는다.
 */

/** 연애·배우자 (자세한 연애 궁합) */
export const LOVE_KINDS = ['lover', 'spouse', 'crush', 'ex'] as const
/** 일 */
export const WORK_KINDS = ['business', 'boss', 'subordinate', 'colleague', 'client'] as const
/** 가족 */
export const FAMILY_KINDS = ['parent', 'child', 'sibling', 'inlaw'] as const
/** 그 외 */
export const ETC_KINDS = ['friend', 'senior', 'neighbour', 'other'] as const

export const RELATION_KINDS = [...LOVE_KINDS, ...WORK_KINDS, ...FAMILY_KINDS, ...ETC_KINDS] as const
export type RelationKind = (typeof RELATION_KINDS)[number]

/** 고르는 화면에서 묶어 보여 주는 단위 */
export const RELATION_GROUPS = [
  { key: 'love', kinds: LOVE_KINDS },
  { key: 'work', kinds: WORK_KINDS },
  { key: 'family', kinds: FAMILY_KINDS },
  { key: 'etc', kinds: ETC_KINDS },
] as const

/** 이 관계는 자세한 연애 궁합으로 본다 */
export function isLoveKind(kind: RelationKind): boolean {
  return (LOVE_KINDS as readonly string[]).includes(kind)
}

/** 관계마다 반가운 기운과 점수 (상대 일간이 나에게 무엇이 되는가) */
const WANT: Record<RelationKind, Partial<Record<Relation, number>>> = {
  // 연애·배우자는 가중치를 두지 않는다 — 합·충이 이미 가장 크게 작용한다
  lover: {},
  spouse: {},
  crush: {},
  ex: {},
  // 일
  business: { wealth: 9, same: 6, output: 3, officer: -3 },
  boss: { officer: 10, resource: 6, same: -4 },
  subordinate: { output: 8, same: 5, officer: -4 },
  colleague: { same: 8, output: 4, officer: -3 },
  client: { wealth: 10, officer: 4, same: -2 },
  // 가족
  parent: { resource: 10, officer: 3, wealth: -3 },
  child: { output: 10, same: 3, officer: -4 },
  sibling: { same: 9, output: 4, officer: -3 },
  inlaw: { resource: 6, officer: 5, same: -2 },
  // 그 외
  friend: { same: 8, output: 6, wealth: -2 },
  senior: { resource: 8, officer: 4, same: -2 },
  neighbour: { same: 5, output: 4 },
  other: { same: 4, output: 3 },
}

/** 관계마다 화면에서 "무엇을 보았는지" 설명하는 코드 */
export const RELATION_FOCUS: Record<RelationKind, Relation[]> = {
  lover: [],
  spouse: [],
  crush: [],
  ex: [],
  business: ['wealth', 'same'],
  boss: ['officer', 'resource'],
  subordinate: ['output', 'same'],
  colleague: ['same', 'output'],
  client: ['wealth', 'officer'],
  parent: ['resource'],
  child: ['output'],
  sibling: ['same'],
  inlaw: ['resource', 'officer'],
  friend: ['same', 'output'],
  senior: ['resource'],
  neighbour: ['same'],
  other: ['same'],
}

export interface RelationCompat extends CompatResult {
  kind: RelationKind
  /** 상대 일간이 나에게 무엇이 되는가 */
  theirRole: Relation
  /** 내 일간이 상대에게 무엇이 되는가 */
  myRole: Relation
  /** 관계 가중치로 더해진 점수 */
  relationPoints: number
}

export function isRelationKind(value: unknown): value is RelationKind {
  return typeof value === 'string' && (RELATION_KINDS as readonly string[]).includes(value)
}

/**
 * 관계를 넣은 궁합. 기본 궁합에 관계 가중치를 더하고 20~99 로 자른다.
 * (연인·배우자는 가중치가 없어 기본 궁합과 같은 값이 나온다.)
 */
export function relationCompat(me: CompatSide, other: CompatSide, kind: RelationKind): RelationCompat {
  const base = compatibility(me, other)
  const myElement = stemElement(me.dayPillar[0]!)
  const theirElement = stemElement(other.dayPillar[0]!)
  const theirRole = relationOf(myElement, theirElement)
  const myRole = relationOf(theirElement, myElement)

  const relationPoints = WANT[kind][theirRole] ?? 0
  const score = Math.max(20, Math.min(99, base.score + relationPoints))
  return { ...base, score, band: bandOf(score), kind, theirRole, myRole, relationPoints }
}
